import "./NavbarStyles.css";
import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import BtnLang from "../BtnLang/BtnLang";
import { t } from "i18next";

const HOME_PATHS = ["/", "/uk", "/ua", "/ukr", "/en"];

export const Navbar = ({
  color = "",
  supportHref = "/help",
  donateHref = "/help#donate",
}) => {
  const location = useLocation();
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const normalizedPath = useMemo(() => {
    const pathname = location.pathname || "/";
    return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  }, [location.pathname]);

  const isHome = HOME_PATHS.includes(normalizedPath);

  const navItems = [
    { to: "/", label: t("nav home") },
    { to: "/projects", label: t("nav projects") },
    { to: "/reports", label: t("nav reports") },
    { to: "/about", label: t("nav about") },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 36);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsNavExpanded(false);
  }, [normalizedPath]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isNavExpanded) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isNavExpanded]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsNavExpanded(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);

    return () => {
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const closeMenu = () => {
    setIsNavExpanded(false);
  };

  return (
    <>
      <header
        className={[
          "sky-navbar",
          isHome ? "sky-navbar--home" : "sky-navbar--inner",
          isScrolled ? "is-scrolled" : "",
          isNavExpanded ? "is-menu-open" : "",
          color,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="sky-navbar__container">
          <Link
            className="sky-navbar__logo"
            to="/"
            aria-label="Sky UA"
            onClick={closeMenu}
          >
            <img src={logo} alt="Sky UA" />
          </Link>

          <nav
            className="sky-navbar__desktop-nav"
            aria-label="Main navigation"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  isActive ? "is-active" : undefined
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="sky-navbar__right">
            <div className="sky-navbar__language">
              <BtnLang />
            </div>

            <button
              type="button"
              className={`sky-navbar__burger ${
                isNavExpanded ? "is-open" : ""
              }`}
              aria-label={isNavExpanded ? "Close menu" : "Open menu"}
              aria-expanded={isNavExpanded}
              aria-controls="sky-mobile-nav"
              onClick={() => {
                setIsNavExpanded((currentValue) => !currentValue);
              }}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <aside
        id="sky-mobile-nav"
        className={`sky-mobile-nav ${
          isNavExpanded ? "is-open" : ""
        }`}
        aria-hidden={!isNavExpanded}
      >
        <div className="sky-mobile-nav__top">
          <Link
            className="sky-mobile-nav__logo"
            to="/"
            aria-label="Sky UA"
            onClick={closeMenu}
          >
            <img src={logo} alt="Sky UA" />
          </Link>

          <div className="sky-mobile-nav__top-right">
            <div className="sky-mobile-nav__language">
              <BtnLang />
            </div>

            <button
              type="button"
              className="sky-mobile-nav__close"
              aria-label="Close menu"
              onClick={closeMenu}
            >
              <span />
              <span />
            </button>
          </div>
        </div>

        <div className="sky-mobile-nav__content">
          <p className="sky-mobile-nav__eyebrow">
            {t("navigation") === "navigation"
              ? "НАВІГАЦІЯ"
              : t("navigation")}
          </p>

          <nav
            className="sky-mobile-nav__links"
            aria-label="Mobile navigation"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  isActive ? "is-active" : undefined
                }
                onClick={closeMenu}
              >
                <span>{item.label}</span>
                <span aria-hidden="true">→</span>
              </NavLink>
            ))}
          </nav>

          <div className="sky-mobile-nav__actions">
            <a
              className="sky-mobile-nav__donate"
              href={donateHref}
              onClick={closeMenu}
            >
              {t("btn get help")}
            </a>

            <a
              className="sky-mobile-nav__help"
              href={supportHref}
              onClick={closeMenu}
            >
              <span>{t("btn need help")}</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        <div className="sky-mobile-nav__footer">
          <span>SKYUA.ORG</span>
          <span aria-hidden="true" />
          <span>UKRAINE</span>
        </div>
      </aside>
    </>
  );
};
